import { Country, Currency, LanguageCode, OrganizationType, TimeZone } from '@navio/nsdk';
import { revalidateLogic } from '@tanstack/react-form';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NavioPage, NoddiBasicCard, useAppForm, zodOptionalPhoneNumberSchema } from 'noddi-ui-web';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { TanstackBrandAsyncSelect } from '../../form/tanstack/features/Brand/TanstackBrandAsyncSelect';
import { TanstackCurrencySelect } from '../../form/tanstack/features/Currency/TanstackCurrencySelect';
import { TanstackLanguageCodeSelect } from '../../form/tanstack/features/Language/TanstackLanguageCodeSelect';
import { TanstackCountrySelect } from '../../form/tanstack/features/Location/TanstackCountrySelect';
import { TanstackTimeZoneSelect } from '../../form/tanstack/features/Location/TanstackTimeZoneSelect';
import { CompanyLookupSection } from '../CompanyLookup/CompanyLookupSection';
import { TanstackOrganizationTypeSelect } from './TanstackOrganizationTypeSelect';
import { OrganizationRoutes } from './types';
import { validateNorwegianOrganizationNumber } from './utils';

const validationSchema = z
  .object({
    name: z.string().min(1, 'Required'),
    nameLegal: z.string(),
    registrationNumber: z.string().min(1, 'Required'),
    currency: z.string().min(1, 'Required'),
    timeZone: z.string().min(1, 'Required'),
    country: z.string().min(1, 'Required'),
    languageCode: z.string().min(1, 'Required'),
    type: z.string().min(1, 'Required'),
    brand: z
      .object({ value: z.number(), label: z.string() })
      .optional()
      .refine((val) => !!val, { message: 'Brand is required' }),
    logo: z.instanceof(File).nullable(),
    email: z.email('Invalid email').or(z.literal('')),
    phoneNumber: zodOptionalPhoneNumberSchema,
    website: z.url('Invalid URL').or(z.literal(''))
  })
  .refine(
    (data) => {
      if (data.country !== 'NO' || !data.registrationNumber) {
        return true;
      }
      return validateNorwegianOrganizationNumber(data.registrationNumber) === undefined;
    },
    { message: 'Invalid Norwegian organization number', path: ['registrationNumber'] }
  );

type FormValues = z.input<typeof validationSchema>;

const defaultValues: FormValues = {
  name: '',
  nameLegal: '',
  registrationNumber: '',
  timeZone: '',
  currency: '',
  country: '',
  languageCode: '',
  type: '',
  brand: undefined,
  logo: null,
  email: '',
  phoneNumber: '',
  website: ''
};

interface Props {
  routes: OrganizationRoutes;
}

export const CreateOrganizationPage = ({ routes }: Props) => {
  const navigate = useNavigate();

  const { mutateAsync: createOrganization } = noddiAsync.usePost({
    type: URLKeys.postOrganization,
    queryConfig: {
      onSuccess: async (data) => {
        navigate(routes.editOrganization.getPath({ id: data.data.id }));
      }
    }
  });

  const form = useAppForm({
    defaultValues,
    validationLogic: revalidateLogic(),
    validators: { onDynamic: validationSchema },
    onSubmit: async ({ value }) => {
      await createOrganization({
        name: value.name,
        ...(value.nameLegal && { nameLegal: value.nameLegal }),
        registrationNumber: value.registrationNumber.replace(/\s/g, ''),
        timeZone: value.timeZone as TimeZone,
        country: value.country as Country,
        currency: value.currency as Currency,
        language: value.languageCode as LanguageCode,
        type: value.type as OrganizationType,
        brandId: value.brand!.value,
        ...(value.logo && { logo: value.logo }),
        contactDetails: {
          ...(value.email && { email: value.email }),
          // Parse to apply the schema's whitespace-trim — TanStack Form never
          // writes transform output back into form state.
          ...(value.phoneNumber && { phoneNumber: zodOptionalPhoneNumberSchema.parse(value.phoneNumber) }),
          ...(value.website && { website: value.website })
        }
      });
    }
  });

  return (
    <NavioPage
      title='Create organization'
      breadcrumbs={[{ title: 'Organizations', path: routes.organizations.getPath() }, { title: 'Create' }]}
    >
      <form.AppForm>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <NoddiBasicCard className='grid grid-cols-1 gap-6 md:grid-cols-2'>
            <div className='col-span-full flex items-end gap-3'>
              <div className='flex-1'>
                <form.AppField name='registrationNumber'>
                  {(field) => <field.Input label='Registration number' />}
                </form.AppField>
              </div>
              <form.Subscribe selector={(state) => state.values.registrationNumber}>
                {(registrationNumber) => (
                  <CompanyLookupSection
                    registrationNumber={registrationNumber}
                    onPopulate={(companyData) => {
                      form.setFieldValue('name', companyData.name);
                      form.setFieldValue('nameLegal', companyData.name);
                    }}
                  />
                )}
              </form.Subscribe>
            </div>
            <form.AppField name='name'>{(field) => <field.Input label='Organization name' />}</form.AppField>
            <form.AppField name='nameLegal'>{(field) => <field.Input label='Legal name' />}</form.AppField>
            <form.AppField name='timeZone'>{() => <TanstackTimeZoneSelect />}</form.AppField>
            <form.AppField name='country'>{() => <TanstackCountrySelect />}</form.AppField>
            <form.AppField name='currency'>{() => <TanstackCurrencySelect />}</form.AppField>
            <form.AppField name='languageCode'>{() => <TanstackLanguageCodeSelect />}</form.AppField>
            <form.AppField name='type'>{() => <TanstackOrganizationTypeSelect />}</form.AppField>
            <form.AppField name='brand'>{() => <TanstackBrandAsyncSelect />}</form.AppField>
          </NoddiBasicCard>
          <NoddiBasicCard className='grid grid-cols-1 gap-6 md:grid-cols-2'>
            <h3 className='col-span-full text-lg font-medium'>Contact details</h3>
            <form.AppField name='email'>{(field) => <field.Input label='Email' />}</form.AppField>
            <form.AppField name='phoneNumber'>{(field) => <field.Input label='Phone number' />}</form.AppField>
            <form.AppField name='website'>{(field) => <field.Input label='Website' />}</form.AppField>
          </NoddiBasicCard>
          <NoddiBasicCard className='flex flex-col gap-4'>
            <form.AppField name='logo'>{(field) => <field.ImageUpload label='Add Logo' />}</form.AppField>
          </NoddiBasicCard>
          <div className='mt-6'>
            <form.SubscribeButton label='Create' />
          </div>
        </form>
      </form.AppForm>
    </NavioPage>
  );
};
